import { useCallback, useEffect, useRef } from 'react';

/**
 * Coalescing de evento realtime (W1.2, extraído da DashboardPage em PERF-01):
 * agenda UM disparo e absorve a rajada pelos próximos `delay` ms. (Não é
 * debounce com reset — esse morre de fome sob stream contínuo de eventos.)
 *
 * Uso canônico: callback de useSupabaseRealtime que refetcha payloads
 * pesados — o patch local trata o evento na hora; o refetch coalescido só
 * reconcilia. Sem coalesce, plataforma autônoma = firehose de eventos =
 * refetch contínuo (PERF-01: dashboard baixava 2.8MB a cada ~2s).
 */
export function useCoalesced(fn: () => void | Promise<void>, delay = 2000): () => void {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return useCallback(() => {
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void fnRef.current();
    }, delay);
  }, [delay]);
}
